/* eslint-disable jsx-a11y/label-has-associated-control */
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useMemo, useState } from "react";
import { UseFieldArrayReturn, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PsTouchup, PsTouchupCandidate } from "../../../../api/model";
import { getNextId } from "../../../../utils/id";
import { toString } from '../../../../utils/string';
import { TextInput } from "../../../atoms/TextInput";
import Modal from "../../../modules/Modal";
import OkModal from '../../../modules/OkModal';
import { PsTouchupSchemaType, psTouchupSchema } from "./constants/variables";

interface Props {
  // モーダル表示・非表示状態。
  isOpen: boolean;

  // モーダル表示・非表示状態設定関数。
  setIsOpen: (isOpen: boolean) => void;

  /** 新規追加モード / 編集モード */
  mode: string;

  /** 操作対象となるタッチアップの index。 */
  psTouchupIndex: number;
}

// PS 編集画面で使用。
/** タッチアップ追加編集ダイアログ。 */
function PsTouchupAddEditDialog(
  {
    isOpen,
    setIsOpen,
    mode,
    psTouchupIndex,
  }: Props
) {

  // i18n 用。
  const {
    t,
    // i18n,
  } = useTranslation();

  // 参照: https://qiita.com/y-yasuda/items/9184380d3320d47bc564
  // PsDetails に置かれた FormProvider から取得。
  const {
    // register, 
    // formState, 
    control: contextControl,
    getValues: contextGetValues,
    // setValue: contextSetValue
  } = useFormContext();

  // touchups 用。
  const {
    // fields: touchupFields,
    append: appendTouchup,
    update: updateTouchup,
    // remove: removeTouchup,
  } = useFieldArray({
    control: contextControl,
    name: 'touchups' as const,
  });

  const touchups = contextGetValues('touchups');
  /** 次の ID 生成のためにタッチアップの ID の配列を取得。 */
  const touchupIds = useMemo(
    () =>
      (Array.isArray(touchups)) ?
        touchups.map(
          (psTouchup: PsTouchup) =>
            (typeof psTouchup.touchup_id === 'number') ?
              psTouchup.touchup_id
              :
              0
        )
        :
        [],
    [touchups]
  );

  const defaultValues: PsTouchupSchemaType = useMemo(
    () =>
      (
        // 新規追加なら Schema からデフォルト値を取得し、編集なら useFormContext から値を取得して defaultValues とする。
        (mode === 'Add') ?
          {
            ...psTouchupSchema.getDefault(),
            // PsDetails でタッチアップの key としているため、unique な値にしたい。
            touchup_id: getNextId(touchupIds),
            // PsDetails で行ごとに表示するため、列ごとに異なる key にしておく。
            touchup_targets: [
              {
                touchup_target_id: 0,
              },
              {
                touchup_target_id: 1,
              },
              {
                touchup_target_id: 2,
              },
            ],
          }
          :
          contextGetValues(`touchups.${psTouchupIndex}`)
      ) as PsTouchupSchemaType,
    [contextGetValues, mode, psTouchupIndex, touchupIds]
  );

  // console.log('defaultValues: ', defaultValues);

  // このダイアログで独立した form を使い、useFormContext と useFieldArray で呼び出し元 (PsDetails) の form の値 (touchups) を変更する。
  // React Hook Form。
  const {
    register,
    formState,
    handleSubmit,
    // reset,
    control,
    getValues,
    // setValue,
    // watch,
  } = useForm<PsTouchupSchemaType>({
    defaultValues,
    resolver: yupResolver(psTouchupSchema),
    criteriaMode: 'all',
    mode: 'all',
  });


  type TouchupTargetUiData = {
    language: string,
    // index: number,

    // useFieldArrayReturn: unknown,
    useFieldArrayReturn?: UseFieldArrayReturn<never> | undefined,

    touchupCandidateIds: number[],
  };

  /** touchup_target ごとの UI 用のデータをまとめる。 */
  const touchupTargetsUiData: TouchupTargetUiData[] = [t('日本語'), t('米語'), t('英語')].map(
    (language: string, languageIndex: number) =>
    (
      {
        language,
        // index: languageIndex,

        // useFieldArrayReturn: undefined,

        /** 次の ID 生成のために各言語の touchup_candidates の ID の配列を取得。 */
        touchupCandidateIds: (Array.isArray(getValues(`touchup_targets.${languageIndex}.touchup_candidates` as never))) ?
          (
            (getValues(`touchup_targets.${languageIndex}.touchup_candidates` as never) as PsTouchupCandidate[]).map(
              (psTouchupCandidate: PsTouchupCandidate) =>
                (typeof psTouchupCandidate.touchup_candidate_id === 'number') ?
                  psTouchupCandidate.touchup_candidate_id
                  :
                  0
            )
          )
          :
          [],
      }
    )
  );

  // Hook なのでトップ階層で呼び出す。

  // 日本語の touchup_candidates 用。
  touchupTargetsUiData[0].useFieldArrayReturn = useFieldArray({
    control,
    name: 'touchup_targets.0.touchup_candidates' as never,
  }) as unknown as UseFieldArrayReturn<never>;
  // 米語の touchup_candidates 用。
  touchupTargetsUiData[1].useFieldArrayReturn = useFieldArray({
    control,
    name: 'touchup_targets.1.touchup_candidates' as never,
  }) as unknown as UseFieldArrayReturn<never>;
  // 英語の touchup_candidates 用。
  touchupTargetsUiData[2].useFieldArrayReturn = useFieldArray({
    control,
    name: 'touchup_targets.2.touchup_candidates' as never,
  }) as unknown as UseFieldArrayReturn<never>;


  // // 日本語の touchup_candidates 用。
  // const {
  //   fields: japaneseTouchupCandidateFields,
  //   append: appendJapaneseTouchupCandidate,
  //   remove: removeJapaneseTouchupCandidate,
  // } = useFieldArray({
  //   control,
  //   name: 'touchup_targets.0.touchup_candidates' as never,
  // });

  // console.log('touchup_targets.0.touchup_candidates: ', getValues('touchup_targets.0.touchup_candidates' as never));

  // /** 日本語の touchup_candidates。 */
  // const japaneseTouchupCandidates = getValues('touchup_targets.0.touchup_candidates' as never);
  // /** 次の ID 生成のために日本語の touchup_candidates の ID の配列を取得。 */
  // const japaneseTouchupCandidateIds = useMemo(
  //   () =>
  //   (
  //     (Array.isArray(japaneseTouchupCandidates)) ?
  //       (
  //         (japaneseTouchupCandidates as PsTouchupCandidate[]).map(
  //           (psTouchupCandidate: PsTouchupCandidate) =>
  //             (typeof psTouchupCandidate.touchup_candidate_id === 'number') ?
  //               psTouchupCandidate.touchup_candidate_id
  //               :
  //               0
  //         )
  //       )
  //       :
  //       []
  //   ),
  //   [japaneseTouchupCandidates]
  // );

  // // console.log('japaneseTouchupCandidateIds: ', japaneseTouchupCandidateIds);

  // // 米語の touchup_candidates 用。
  // const {
  //   fields: americanEnglishTouchupCandidateFields,
  //   append: appendAmericanEnglishTouchupCandidate,
  //   remove: removeAmericanEnglishTouchupCandidate,
  // } = useFieldArray({
  //   control,
  //   name: 'touchup_targets.1.touchup_candidates' as never,
  // });

  // /** 米語の touchup_candidates。 */
  // const americanEnglishTouchupCandidates = getValues('touchup_targets.1.touchup_candidates' as never);
  // /** 次の ID 生成のために米語の touchup_candidates の ID の配列を取得。 */
  // const americanEnglishTouchupCandidateIds = useMemo(
  //   () =>
  //   (
  //     (Array.isArray(americanEnglishTouchupCandidates)) ?
  //       (
  //         (americanEnglishTouchupCandidates as PsTouchupCandidate[]).map(
  //           (psTouchupCandidate: PsTouchupCandidate) =>
  //             (typeof psTouchupCandidate.touchup_candidate_id === 'number') ?
  //               psTouchupCandidate.touchup_candidate_id
  //               :
  //               0
  //         )
  //       )
  //       :
  //       []
  //   ),
  //   [americanEnglishTouchupCandidates]
  // );

  // // 英語の touchup_candidates 用。
  // const {
  //   fields: englishTouchupCandidateFields,
  //   append: appendEnglishTouchupCandidate,
  //   remove: removeEnglishTouchupCandidate,
  // } = useFieldArray({
  //   control,
  //   name: 'touchup_targets.2.touchup_candidates' as never,
  // });

  // /** 英語の touchup_candidates。 */
  // const englishTouchupCandidates = getValues('touchup_targets.2.touchup_candidates' as never);
  // /** 次の ID 生成のために英語の touchup_candidates の ID の配列を取得。 */
  // const englishTouchupCandidateIds = useMemo(
  //   () =>
  //   (
  //     (Array.isArray(englishTouchupCandidates)) ?
  //       (
  //         (englishTouchupCandidates as PsTouchupCandidate[]).map(
  //           (psTouchupCandidate: PsTouchupCandidate) =>
  //             (typeof psTouchupCandidate.touchup_candidate_id === 'number') ?
  //               psTouchupCandidate.touchup_candidate_id
  //               :
  //               0
  //         )
  //       )
  //       :
  //       []
  //   ),
  //   [englishTouchupCandidates]
  // );

  // console.log('touchups.index: ', contextGetValues(`touchups.${psTouchupIndex}`));

  /** handleSubmit() の SubmitErrorHandler が呼ばれた時、その引数を set する。undefined でない場合、モーダルで内容を表示する。 */
  const [handleSubmitErrors, setHandleSubmitErrors] = useState<unknown>(undefined);

  return (
    <>
      <Modal
        active={isOpen}
        additionalClassName='popupfr865'
        displayCloseButton
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <div className="pop-tablefr2 pop24">
          <h3>{t('タッチアップ')}{(mode === 'Add') ? t('追加') : t('編集')}</h3>
          <table className="poptable1 w230 poptable3">
            <tbody>
              <tr>
                <th>
                  <label>{t('項目名')}</label>
                  &nbsp;
                  <span
                    style={{
                      backgroundColor: 'dodgerblue',
                      color: 'white',
                    }}
                  >
                    {t('必須')}
                  </span>
                </th>
                <td>
                  <TextInput
                    name={'touch_up' as never}
                    register={register}
                    errors={formState.errors}
                  />
                </td>
              </tr>
              <tr>
                <th>
                  <label>{t('判断情報')}</label>
                  &nbsp;
                  <span
                    style={{
                      backgroundColor: 'dodgerblue',
                      color: 'white',
                    }}
                  >
                    {t('必須')}
                  </span>
                </th>
                <td>
                  <TextInput
                    name={'judgement_information' as never}
                    register={register}
                    errors={formState.errors}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {/* TODO?: touchup_targets を Fields 含め配列にする? */}

          {
            touchupTargetsUiData.map(
              (touchupTargetUiData: TouchupTargetUiData, touchupTargetUiDataIndex: number) =>
              (
                <React.Fragment
                  key={touchupTargetUiData.language}
                >
                  <h4>{touchupTargetUiData.language}{t('OM')}</h4>
                  <table className="poptable1 w230 poptable3">
                    <tbody>
                      <tr>
                        <th>
                          <label>{t('テキストの部分')}</label>
                        </th>
                        <td>
                          <TextInput
                            name={`touchup_targets.${touchupTargetUiDataIndex}.target` as never}
                            register={register}
                            errors={formState.errors}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>
                          <label>{t('書換単語候補')}</label>
                        </th>
                        <td>
                          <span
                            className="cursor"
                            onClick={
                              () => {
                                // 書換単語候補を追加。
                                touchupTargetUiData.useFieldArrayReturn?.append(
                                  {
                                    // unique な ID にする。
                                    touchup_candidate_id: getNextId(touchupTargetUiData.touchupCandidateIds),
                                    candidate: '',
                                  }
                                );
                              }
                            }
                          >
                            <i className="fa-solid fa-circle-plus" />
                            {t('追加')}
                          </span>
                          {
                            touchupTargetUiData.useFieldArrayReturn?.fields.map(
                              (touchupCandidate: Record<'id', string>, touchupCandidateIndex: number) =>
                              (
                                <span
                                  key={touchupCandidate.id}
                                  className="deletefr"
                                >
                                  <TextInput
                                    name={`touchup_targets.${touchupTargetUiDataIndex}.touchup_candidates.${touchupCandidateIndex}.candidate` as never}
                                    register={register}
                                    errors={formState.errors}
                                  />
                                  <span
                                    onClick={
                                      () => {
                                        // 書換単語候補を削除。
                                        touchupTargetUiData.useFieldArrayReturn?.remove(touchupCandidateIndex);
                                      }
                                    }
                                  >
                                    <i className="fa-solid fa-circle-xmark" />
                                    {t('削除')}
                                  </span>
                                </span>
                              )
                            )
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </React.Fragment>
              )
            )
          }

          {/* <h4>{t('日本語OM')}</h4>
        <table className="poptable1 w230 poptable3">
          <tbody>
            <tr>
              <th>
                <label>{t('テキストの部分')}</label>
              </th>
              <td>
                <TextInput
                  name={'touchup_targets.0.target' as never}
                  register={register}
                  errors={formState.errors}
                />
              </td>
            </tr>
            <tr>
              <th>
                <label>{t('書換単語候補')}</label>
              </th>
              <td>
                <span
                  className="cursor"
                  onClick={
                    () => {
                      // console.log('getNextId(japaneseTouchupCandidateIds): ', getNextId(japaneseTouchupCandidateIds));

                      // 書換単語候補を追加。
                      appendJapaneseTouchupCandidate(
                        {
                          // unique な ID にする。
                          touchup_candidate_id: getNextId(japaneseTouchupCandidateIds),
                          candidate: '',
                        }
                      );
                    }
                  }
                >
                  <i className="fa-solid fa-circle-plus" />
                  {t('追加')}
                </span>
                {
                  japaneseTouchupCandidateFields.map(
                    (japaneseTouchupCandidate: Record<'id', string>, japaneseTouchupCandidateIndex: number) =>
                    (
                      <span
                        key={japaneseTouchupCandidate.id}
                        className="deletefr"
                      >
                        <TextInput
                          name={`touchup_targets.0.touchup_candidates.${japaneseTouchupCandidateIndex}.candidate` as never}
                          register={register}
                          errors={formState.errors}
                        />
                        <span
                          onClick={
                            () => {
                              // 書換単語候補を削除。
                              removeJapaneseTouchupCandidate(japaneseTouchupCandidateIndex);
                            }
                          }
                        >
                          <i className="fa-solid fa-circle-xmark" />
                          {t('削除')}
                        </span>
                      </span>
                    )
                  )
                }
              </td>
            </tr>
          </tbody>
        </table>
        <h4>{t('米語OM')}</h4>
        <table className="poptable1 w230 poptable3">
          <tbody>
            <tr>
              <th>
                <label>{t('テキストの部分')}</label>
              </th>
              <td>
                <TextInput
                  name={'touchup_targets.1.target' as never}
                  register={register}
                  errors={formState.errors}
                />
              </td>
            </tr>
            <tr>
              <th>
                <label>{t('書換単語候補')}</label>
              </th>
              <td>
                <span
                  className="cursor"
                  onClick={
                    () => {
                      // 書換単語候補を追加。
                      appendAmericanEnglishTouchupCandidate(
                        {
                          // unique な ID にする。
                          touchup_candidate_id: getNextId(americanEnglishTouchupCandidateIds),
                          candidate: '',
                        }
                      );
                    }
                  }
                >
                  <i className="fa-solid fa-circle-plus" />
                  {t('追加')}
                </span>
                {
                  americanEnglishTouchupCandidateFields.map(
                    (americanEnglishTouchupCandidate: Record<'id', string>, americanEnglishTouchupCandidateIndex: number) =>
                    (
                      <span
                        key={americanEnglishTouchupCandidate.id}
                        className="deletefr"
                      >
                        <TextInput
                          name={`touchup_targets.1.touchup_candidates.${americanEnglishTouchupCandidateIndex}.candidate` as never}
                          register={register}
                          errors={formState.errors}
                        />
                        <span
                          onClick={
                            () => {
                              // 書換単語候補を削除。
                              removeAmericanEnglishTouchupCandidate(americanEnglishTouchupCandidateIndex);
                            }
                          }
                        >
                          <i className="fa-solid fa-circle-xmark" />
                          {t('削除')}
                        </span>
                      </span>
                    )
                  )
                }
              </td>
            </tr>
          </tbody>
        </table>
        <h4>{t('英語OM')}</h4>
        <table className="poptable1 w230 poptable3">
          <tbody>
            <tr>
              <th>
                <label>{t('テキストの部分')}</label>
              </th>
              <td>
                <TextInput
                  name={'touchup_targets.2.target' as never}
                  register={register}
                  errors={formState.errors}
                />
              </td>
            </tr>
            <tr>
              <th>
                <label>{t('書換単語候補')}</label>
              </th>
              <td>
                <span
                  className="cursor"
                  onClick={
                    () => {
                      // 書換単語候補を追加。
                      appendEnglishTouchupCandidate(
                        {
                          // unique な ID にする。
                          touchup_candidate_id: getNextId(englishTouchupCandidateIds),
                          candidate: '',
                        }
                      );
                    }
                  }
                >
                  <i className="fa-solid fa-circle-plus" />
                  {t('追加')}
                </span>
                {
                  englishTouchupCandidateFields.map(
                    (englishTouchupCandidate: Record<'id', string>, englishTouchupCandidateIndex: number) =>
                    (
                      <span
                        key={englishTouchupCandidate.id}
                        className="deletefr"
                      >
                        <TextInput
                          name={`touchup_targets.2.touchup_candidates.${englishTouchupCandidateIndex}.candidate` as never}
                          register={register}
                          errors={formState.errors}
                        />
                        <span
                          onClick={
                            () => {
                              // 書換単語候補を削除。
                              removeEnglishTouchupCandidate(englishTouchupCandidateIndex);
                            }
                          }
                        >
                          <i className="fa-solid fa-circle-xmark" />
                          {t('削除')}
                        </span>
                      </span>
                    )
                  )
                }
              </td>
            </tr>
          </tbody>
        </table> */}

        </div>

        <div className="btnfr btnfr2w mb0">
          <div>
            <span className="btn2 btnbg-g"
              onClick={
                () => {
                  setIsOpen(false);
                }
              }
            >
              <span className="modal-close2">{t('キャンセル')}</span>
            </span>
            <span>
              <input
                type="button"
                value={(mode === 'Add') ? toString(t('追加')) : toString(t('更新'))}
                className="btn1 lsp1"
                disabled={!formState.isValid}
                onClick={
                  handleSubmit(
                    (data: unknown) => {
                      // console.log('handleSubmit data: ', data);
                      // console.log('getValues(): ',
                      //   getValues()
                      // );

                      if (mode === 'Add') {
                        // 新規追加。
                        appendTouchup(data);
                      }
                      else if (mode === 'Edit') {
                        // 更新。
                        updateTouchup(psTouchupIndex, data);
                      }

                      setIsOpen(false);
                    },
                    (errors: unknown) => {
                      // console.log('handleSubmit errors: ', errors);

                      // モーダルでエラー メッセージを表示する。
                      setHandleSubmitErrors(errors);
                    }
                  )
                }
              />
            </span>
          </div>
        </div>

      </Modal>

      {
        // handleSubmit() でエラーがあった場合。
        (handleSubmitErrors)
        &&
        (
          <OkModal
            onClickOk={
              () => {
                // handleSubmitErrors をクリアー。
                setHandleSubmitErrors(undefined);
              }
            }
          >
            {/* {JSON.stringify(handleSubmitErrors)} */}
            {
              // formState.errors[]?.message
              Object.getOwnPropertyNames(formState.errors).map(
                (propName: string) => {
                  const errorMessage = toString(formState.errors[propName]?.message);
                  return (
                    <div>
                      {
                        // // スキーマ定義時に設定した message を置き換えて表示する。
                        // (errorMessage === 'ps_no_prefix.required()') ?
                        //   t('管理No.接頭語は必須項目です。')
                        //   :
                        //   (errorMessage === 'derivations.min(1)') ?
                        //     t('派生は最低でも1つは登録されている必要があります。')
                        //     :
                        //     (errorMessage === 'text_conditions.min(1)') ?
                        //       t('テキスト確定条件は最低でも1つは登録されている必要があります。')
                        //       :
                        errorMessage
                      }
                    </div>
                  );
                }
              )
            }
          </OkModal>
        )
      }

    </>
  );
}

export default PsTouchupAddEditDialog;